import "./employee-bulk-reassign.scss";

import { autoinject, bindable, bindingMode, customElement, observable, computedFrom } from "aurelia-framework";
import { ValidationController, ValidationRules } from "aurelia-validation";
import { DialogController } from "aurelia-dialog";

import nameOf from "../../../common/name-of";
import type { IPatientBulkReassignVisitPayload, IHosBulkReassign } from "../../../interfaces/i-bulk-reassign-visit";
import { ApplicationEnum, ConflictTypeEnum } from "../../../enums/enums";
import { ToastrService } from "../../../services/toastr.service";
import { UserService } from "../../../services/user-service";
import type {
    IGetAllUsersResponse,
    IGetScheduleEmployeeResponse,
    IGetUserResponse
} from "./../../../interfaces/i-user";
import { TaskService, ITaskByPatientResponse } from "../../../services/task.service";
import { BulkReassign } from "../../../models/bulk-reassign";
import type { ITypeaheadOptions } from "../../../interfaces/i-typeahead";
import type { IValidateCustomElement } from "../../../interfaces/i-validate-custom-element";
import {
    IEmployeeDetails,
    ITrackingReasonDetails,
    ITrackingTypeDetails
} from "../../../interfaces/i-home-care-reassign";

@autoinject
@customElement("patient-bulk-reassign")
export class PatientBulkReassign {
    public visitDetails: BulkReassign = new BulkReassign();
    @bindable({ defaultBindingMode: bindingMode.toView })
    public application: ApplicationEnum;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public agencyId: string;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public patientId: string;
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public selectedTaskId: any[] = [];
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public selectedUserId: string[] = [];
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public hosTask: any[] = [];
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public palTask: any[] = [];
    @bindable({ defaultBindingMode: bindingMode.fromView })
    public closeReassignForm: () => void;
    @bindable({ defaultBindingMode: bindingMode.fromView })
    public pageReset: () => void;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public dateFrom: string;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public dateTo: string;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public branchId: string;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public showAllByDefault: boolean = true;
    private _toastrService: ToastrService;
    private _userService: UserService;
    private _taskService: TaskService;
    public isLoading: boolean = false;
    public isSaving: boolean = false;
    public dialogController: DialogController;
    public controller: ValidationController = null;
    public isApplicationHomeCare: boolean = false;
    public trackingTypes: ITrackingTypeDetails[] = [];
    public trackingReasons: ITrackingReasonDetails[] = [];
    public cpyTrackingReason: ITrackingReasonDetails[] = [];
    public isEmployeeListLoading: boolean = false;
    public selectedConflictTypes: number[] = [ConflictTypeEnum.NoConflict];
    public employeeList: IGetUserResponse[] = [];
    public employeeLists: IEmployeeDetails[] = [];
    @observable({ changeHandler: nameOf<PatientBulkReassign>("selectedEmployee") })
    public employee: ITypeaheadOptions;

    public constructor(
        validationController: ValidationController,
        toastrService: ToastrService,
        userService: UserService,
        dialogController: DialogController,
        tasksService: TaskService
    ) {
        this.controller = validationController;
        this._toastrService = toastrService;
        this._userService = userService;
        this.dialogController = dialogController;
        this._toastrService = toastrService;
        this._taskService = tasksService;
    }

    public employeeValidation: IValidateCustomElement = {
        required: true,
        displayName: "Employee"
    };

    public selectedEmployee(newValue: ITypeaheadOptions) {
        if (newValue) {
            this.visitDetails.EmployeeTo = newValue.value;
        }
    }

    public attached() {
        if (this.application == ApplicationEnum.HomeCare) {
            this.getHomeCareEmployee();
        } else {
            this.getEmployee("");
        }
    }

    public async bind() {
        if (this.application == ApplicationEnum.HomeCare) {
            this.isApplicationHomeCare = true;
        }

        if (this.isApplicationHomeCare) {
            this.trackingReasons = await this._taskService.getTrackingReasons({
                AgencyId: this.agencyId,
                Application: this.application,
                CategoryId: 1
            });

            this.trackingTypes = await this._taskService.getTrackingTypes({
                AgencyId: this.agencyId,
                Application: this.application,
                CategoryId: 1
            });
        }
    }

    public selectedType() {
        if (!!this.visitDetails.TypeId) {
            this.cpyTrackingReason = this.trackingReasons.filter((reasonType) => {
                return reasonType.TypeId == (this.visitDetails.TypeId as any);
            });
        } else {
            this.visitDetails.ReasonId = null;
            this.cpyTrackingReason = [];
        }
    }

    public async getEmployee(term: string) {
        this.employeeList = await this._userService.getUserByBranch({
            application: this.application,
            agencyId: this.agencyId,
            branchId: this.branchId,
            page: 1,
            pageSize: 40,
            term: term
        });
    }

    public async getHomeCareEmployee() {
        try {
            this.isEmployeeListLoading = true;
            const employees = await this._taskService.homecareEmployeePreference({
                PatientId: this.patientId,
                AgencyId: this.agencyId,
                SchedulerConflictTypes: this.selectedConflictTypes,
                Application: ApplicationEnum.HomeCare,
                LocationId: this.branchId,
                ignoreConflictTypes: true
            });
            this.employeeLists = employees.filter((employee) => {
                return employee.Id != this.patientId;
            });
        } catch (e) {
            throw e;
        } finally {
            this.isEmployeeListLoading = false;
        }
    }

    public filterEmployee(filter: string) {
        if (filter) {
            if (this.application == ApplicationEnum.HomeCare) {
                const filterHCEmployeeList = this.employeeLists.filter((employee) => {
                    const fullName = `${employee.FirstName} ${employee.LastName}`;
                    return fullName.toLowerCase().includes(filter.toLowerCase());
                });
                return filterHCEmployeeList?.map((item) => {
                    return {
                        name: `${item.FirstName} ${item.LastName}`,
                        value: item.Id
                    };
                });
            } else {
                this.getEmployee(filter);
                const filterEmployeeFromList = this.employeeList.filter((employee) => {
                    const fullName = `${employee.firstName} ${employee.lastName}`;
                    return fullName.toLowerCase().includes(filter.toLowerCase());
                });
                return filterEmployeeFromList?.map((item) => {
                    return {
                        name: `${item.firstName} ${item.lastName}`,
                        value: item.id
                    };
                });
            }
        }
        return [];
    }

    public cancel() {
        this.closeReassignForm();
    }

    public async reassignVisit() {
        this.visitDetails.initValidations();
        const res = await this.controller.validate();
        if (!res.valid) {
            return;
        }
        if (!!this.visitDetails) {
            try {
                this.isSaving = true;
                const taskIds = [] as string[];
                const userIds = [] as string[];
                this.selectedTaskId.map((task) => {
                    taskIds.push(task.Id);
                    userIds.push(task.UserId);
                    if (this.application == ApplicationEnum.AxxessHospiceFE) {
                        this.hosTask.push({ TaskId: task.Id, EmployeeFrom: task.UserId });
                    }
                    if (this.application == ApplicationEnum.AxxessPalliative) {
                        this.palTask.push({ TaskId: task.Id, EmployeeFrom: task.UserId });
                    }
                });

                if (this.application == ApplicationEnum.AxxessHospiceFE) {
                    // const HosTaskIds: IHosBulkReassign = {
                    //     TaskId: this.hosTaskId,
                    //     EmployeeFrom: this.hosUserId
                    // };
                    const HosPayload: IPatientBulkReassignVisitPayload = {
                        PatientId: this.patientId,
                        AgencyId: this.agencyId,
                        Application: this.application,
                        EmployeeTo: this.visitDetails.EmployeeTo,
                        HospiceTaskIds: this.hosTask
                    };
                    await this._taskService.patientBulkReassign(HosPayload);
                } else if (this.application == ApplicationEnum.AxxessPalliative) {
                    const PalPayload: IPatientBulkReassignVisitPayload = {
                        PatientId: this.patientId,
                        AgencyId: this.agencyId,
                        Application: this.application,
                        EmployeeTo: this.visitDetails.EmployeeTo,
                        PalliativeTaskIds: this.palTask
                    };
                    await this._taskService.patientBulkReassign(PalPayload);
                } else if (this.application == ApplicationEnum.HomeCare) {
                    const homeCarePayload: IPatientBulkReassignVisitPayload = {
                        PatientId: this.patientId,
                        AgencyId: this.agencyId,
                        Application: this.application,
                        EmployeeTo: this.visitDetails.EmployeeTo,
                        StartDate: this.dateFrom,
                        EndDate: this.dateTo,
                        TaskIds: taskIds,
                        ReasonId: this.visitDetails.ReasonId,
                        ReasonMessage: ""
                    };
                    await this._taskService.patientBulkReassign(homeCarePayload);
                } else {
                    const reassignPayload: IPatientBulkReassignVisitPayload = {
                        PatientId: this.patientId,
                        AgencyId: this.agencyId,
                        Application: this.application,
                        EmployeeFrom: userIds,
                        EmployeeTo: this.visitDetails.EmployeeTo,
                        StartDate: this.dateFrom,
                        EndDate: this.dateTo,
                        TaskIds: taskIds
                    };
                    await this._taskService.patientBulkReassign(reassignPayload);
                }

                this._toastrService.success({
                    title: `Visit Reassigned`,
                    message: `Visit has been successfully Reassigned.`
                });
                // trigger callback
                this.cancel();
                this.pageReset();
                this.hosTask = [];
            } catch (e) {
                this._toastrService.error({
                    title: `Visit Reassigning Failed`,
                    message: `Reassigning Visit Operation Failed, Please Contact Axxess if issue still persists.`
                });
            } finally {
                this.isSaving = false;
                this.hosTask = [];
            }
        }
    }
}
